import { css, Interpolation, Theme } from '@emotion/react'
import { mdiWhiteBalanceSunny } from '@mdi/js'
import Icon from '@mdi/react'
import { memo, useCallback } from 'react'
import type { ColorScheme } from '../../../types'
import { setColorScheme } from '../../actions/app'
import { SettingsType } from '../../constants/enums'
import { COLOR_SCHEME_TYPES } from '../../mfcLogic/ueopts'
import { event } from '../../utilities/analytics'
import IconButton from '../Buttons/IconButton'
import { useSettingSelector } from '../Hooks/useSettings'
import DarkMode from '../Icons/DarkMode'
import { maxWindowWidth } from '../styles/breakpoints'
import { palette } from '../styles/colors'

const rootStyles = css({
    display: 'flex',
    background: palette.colorSchemeSwitchTrack,
    borderRadius: 24,
})

const buttonStyles = css({
    border: 0,
    padding: 2,
})

const iconStyles = css({
    borderRadius: '100%',
    padding: 2,
})

const iconSelectedStyles = css({
    border: `1px solid ${palette.backgroundReverse}`,
})

const autoStyles = css({
    color: palette.backgroundReverse,
    fontWeight: 'bold',
    margin: '0 2px',
    fontSize: 9,
    fontFamily: 'Arial',
    letterSpacing: 'normal',
    border: 0,
    padding: 2,
    [maxWindowWidth(960)]: {
        padding: '2px 6px',
    },
})

const autoSelectedStyles = css({
    borderBottom: `1px solid ${palette.backgroundReverse}`,
})

const registerEvent = (colorScheme: ColorScheme, eventLocation) => {
    event({
        action: `color_scheme_toggle__${eventLocation}_click_${colorScheme}`,
        category: 'user_interactions',
        label: 'interaction',
        value: colorScheme,
    })
}

interface DarkModeToggleProps {
    container: string
    testid?: string
    styleOverrides?: Interpolation<Theme>
}

const DarkModeToggle = memo<DarkModeToggleProps>((props) => {
    const { container, testid = 'Toggle__DarkMode', styleOverrides } = props
    const [colorScheme] = useSettingSelector(
        SettingsType.DEFAULT,
        'colorScheme',
    )

    const setDarkModeOn = useCallback(() => {
        setColorScheme(COLOR_SCHEME_TYPES.dark, true)
        registerEvent('dark', container)
    }, [container])

    const setAutoDarkMode = useCallback(() => {
        setColorScheme(COLOR_SCHEME_TYPES.system, true)
        registerEvent('system', container)
    }, [container])

    const setDarkModeOff = useCallback(() => {
        setColorScheme(COLOR_SCHEME_TYPES.light, true)
        registerEvent('light', container)
    }, [container])

    return (
        <div data-testid={testid} css={[rootStyles, styleOverrides]}>
            <IconButton
                onClick={setDarkModeOff}
                css={buttonStyles}
                title="Light Mode"
                name="Light Mode"
            >
                <Icon
                    path={mdiWhiteBalanceSunny}
                    css={[
                        iconStyles,
                        colorScheme === COLOR_SCHEME_TYPES.light &&
                            iconSelectedStyles,
                    ]}
                    size={5 / 6}
                />
            </IconButton>
            <IconButton
                onClick={setAutoDarkMode}
                css={autoStyles}
                title="System Default"
                name="System Default"
            >
                <span
                    css={
                        colorScheme === COLOR_SCHEME_TYPES.system &&
                        autoSelectedStyles
                    }
                >
                    AUTO
                </span>
            </IconButton>
            <IconButton
                onClick={setDarkModeOn}
                css={buttonStyles}
                title="Dark Mode"
                name="Dark Mode"
            >
                <DarkMode
                    css={[
                        iconStyles,
                        colorScheme === COLOR_SCHEME_TYPES.dark &&
                            iconSelectedStyles,
                    ]}
                    fontSize="small"
                />
            </IconButton>
        </div>
    )
})

export default DarkModeToggle
